/**
 * stageModel.js — the two pure decisions behind the Case Home stage carousel.
 *
 * ★★★ WHY THIS FILE EXISTS. Both functions used to live inside JSX — one in
 * StagePager.jsx, one as an IIFE in CaseHome.jsx — so neither could be imported
 * by a gate, and neither was ever measured. Between them they produced FIVE of
 * the fourteen Build 37 reports:
 *
 *   #10 #11 #12 #13  every stage card printed its own subtitle as its body
 *   #1               the carousel parked on Detect while the case was waiting
 *                    on Diagnose
 *
 * An arm on source text is a guess; an arm that imports the function and calls
 * it is a measurement. `scripts/check-stage-model.mjs` does the second thing,
 * which is only possible because this file is plain JS with no JSX in it. The
 * MD panel reached the same conclusion and the same shape in shell/stageModel.js.
 * (Its one import is another plain-JS file, with the extension, so node can load it.)
 */
import { diagnosisTextsOf } from '../../utils/diagnosisText.js'

/**
 * Which stage should the carousel park on?
 *
 * ★★ THE PRODUCT DEFINITION OF "ACTIVE", SETTLED TWICE. v242.87.0 answered TF
 *   b34 #3 by arguing the card was already right — "at case phase 4-5 the
 *   genuinely active step IS Detect". He filed it again against Build 37. He is
 *   not describing the last step the patient can still EDIT; he is describing
 *   THE STEP THE CASE IS WAITING ON. Those differ for the whole of phases 4-5,
 *   which is most of a case's life.
 *
 *   The old order made the difference unreachable rather than merely wrong:
 *     available && !complete  -> Detect   (CaseHome keeps Detect available and
 *                                          not-complete until phase 6)
 *     pending || needsReview  -> Diagnose  <- the answer he wants
 *   findIndex matched Detect first and returned, so the second predicate — which
 *   already computed the right answer — never ran.
 *
 *   Now a DOWNSTREAM step waiting on someone else outranks an upstream step the
 *   patient may still edit. Upstream-waiting never outranks an active step, so
 *   phases 1-3 are untouched and phase 6 returns what it always did.
 */
export function activeStageIndex(sections) {
  if (!Array.isArray(sections) || sections.length === 0) return 0
  // ★★ s101 (Dr. Rami, 2026-10-12): a step already SUBMITTED but still open for additions (Detect
  //   after the share: "View / Add") is not the next step — the patient was being sent back into a
  //   Detect they had finished. It stays tappable; it is no longer where Case Home lands.
  const active = sections.findIndex((s) => s && s.available && !s.complete && !s.submitted)
  const waiting = sections.findIndex((s) => s && (s.pending || s.needsReview || s.externalUpload))
  if (waiting >= 0 && waiting > active) return waiting
  if (active >= 0) return active
  if (waiting >= 0) return waiting
  const submitted = sections.map((s) => !!(s && s.submitted)).lastIndexOf(true)
  if (submitted >= 0) {
    const after = sections.findIndex((s, i) => i > submitted && s && (s.viewable || s.available))
    return after >= 0 ? after : submitted
  }
  const lastComplete = sections.map((s) => !!(s && s.complete)).lastIndexOf(true)
  return lastComplete >= 0 ? Math.min(lastComplete, sections.length - 1) : 0
}

/**
 * The longest COMPLETE phrase that fits the card — never a truncation.
 *
 * ★★★ v242.94.0 — "the chief complaint line is concise! needs to fit, no '...'".
 *   Two earlier cuts both got this wrong in the same direction: they started
 *   from the full sentence and removed characters from the end. v242.92.0 cut
 *   mid-word ("...ongoing for ap…"); v242.93.0 cut on a word boundary but still
 *   ended in an ellipsis. An ellipsis on a chief complaint is a promise that
 *   there is more and you cannot see it — on a card whose whole job is to tell
 *   the patient what this case is about, that is the wrong shape entirely.
 *
 *   So this does not truncate. It builds UP: split the sentence into clauses and
 *   keep whole clauses while they still fit. A clinical one-liner is already
 *   written comma-first — "Left ear pain, pressure, and muffled hearing with
 *   intermittent tinnitus, ongoing for approximately two years" — so the leading
 *   clauses ARE the chief complaint and the trailing ones are the history. Taking
 *   a prefix of clauses is not lossy in the way taking a prefix of characters is.
 *
 *   Worked: that sentence at budget 84 yields
 *     "Left ear pain, pressure, and muffled hearing with intermittent tinnitus"
 *   — 71 characters, grammatical, complete, no ellipsis.
 *
 * ★ If not even the first clause fits, this returns null and the CALLER falls
 *   back to something shorter (focusAreaName is a two-word label and always
 *   fits). Returning null is the honest answer: better a less specific complete
 *   phrase than a specific broken one.
 */
const fit = (v, n = 84) => {
  const str = typeof v === 'string' ? v.trim().replace(/\s+/g, ' ') : ''
  if (!str) return null
  if (str.length <= n) return str

  const build = (parts) => {
    let out = ''
    for (const part of parts) {
      const next = (out + (out ? ' ' : '') + part).trim()
      if (next.length > n) break
      out = next
    }
    out = out.replace(/[\s,;:.–—-]+$/, '')
    return out.length ? out : null
  }

  // Pass 1 — punctuation clauses, keeping the separator so it reads as written.
  const byPunctuation = build(str.split(/(?<=[,;:])\s+|\s+(?=[—–]\s)|(?<=\.)\s+/))
  if (byPunctuation) return byPunctuation

  // ★ Pass 2 — a sentence with no commas at all. "Persistent left ear fullness
  //   and muffled hearing THAT has not improved with antibiotics over two years"
  //   is one 110-character clause, so pass 1 returns nothing and the caller
  //   would drop to the taxonomy label ("Ear Pain") — complete, but far less
  //   than we know. A subordinating conjunction is exactly where the complaint
  //   ends and the history begins, so cutting there keeps a whole phrase.
  //   Coordinators (and / with / or) are deliberately NOT in this list: they
  //   usually join parts of the complaint itself.
  const bySubordinator = build(str.split(/\s+(?=(?:that|which|who|because|since|after|before|while|though|although)\s)/i))
  if (bySubordinator) return bySubordinator

  return null
}

// These fields have been observed arriving as JSON STRINGS as well as objects —
// visitPrepPDF.js:244 parses defensively for exactly the same reason.
const obj = (v) => {
  if (!v) return null
  if (typeof v === 'object') return v
  if (typeof v === 'string') { try { return JSON.parse(v) } catch { return null } }
  return null
}

const text = (v) => (typeof v === 'string' ? v : (v && (v.exam || v.name || v.title || v.description)) || null)

/**
 * The body line for each stage card.
 *
 * ★★ EVERY VALUE COMES FROM DATA THE CLIENT ALREADY HAS. GET /cases is
 *   `select=*` (api/index.py:15480), so patient_summary and pre_visit_summary
 *   come down with the row; what was missing was the store transform.
 *
 * ★★★ AND THE ONE THAT COULD DO HARM: Diagnose is GATED ON FINALISATION.
 *   CaseHome.jsx:341 already warns that `diagnose.diagnosis` "can come from AI
 *   pre-analysis during Describe". Printing that on the Diagnose card would show
 *   a patient a machine's guess in the slot reserved for their doctor's answer.
 *   It renders only when the case is finalised, and prefers the coded billing
 *   description over free text, because that one a doctor signed.
 *
 * @param {object} currentCase
 * @param {{ isCompleted?: boolean, t?: (k: string, d?: any) => string, carePlan?: object|null }} ctx
 */
export function resolveStageBlurbs(currentCase, ctx = {}) {
  const empty = { describe: null, detect: null, diagnose: null, deliver: null, resolved: false }
  if (!currentCase) return empty
  const { isCompleted = false, carePlan = null } = ctx
  const t = typeof ctx.t === 'function' ? ctx.t : (_k, d) => (typeof d === 'string' ? d : '')

  // -- DESCRIBE: the patient's own reason for the visit ----------------------
  const pvs = obj(currentCase.preVisitSummary)
  const handoff = obj(pvs && pvs.handoff)
  const describe = fit(handoff && handoff.reasonForVisit) || fit(currentCase.focusAreaName)

  // -- DETECT: the examination that matters most -----------------------------
  const ps = obj(currentCase.patientSummary)
  const expect = ps && Array.isArray(ps.detectPrep && ps.detectPrep.whatToExpect) ? ps.detectPrep.whatToExpect : []
  const essentials = ps && Array.isArray(ps.examinationEssentials) ? ps.examinationEssentials : []
  const detect = fit(text(expect.find((w) => text(w)))) || fit(text(essentials[0]))

  // -- DIAGNOSE: only once a doctor has finalised it -------------------------
  const dx = currentCase.diagnose || {}
  const codes = Array.isArray(dx.billingCodes) ? dx.billingCodes : []
  const selected = Array.isArray(dx.selectedDiagnoses) ? dx.selectedDiagnoses : []
  // R14 / PROD-260 (patient P1): the diagnosis TEXT only — this used to fall back to the raw
  // ICD-10 code when a billing code carried no description.
  const named = fit(diagnosisTextsOf(codes)[0])
    || fit(typeof dx.diagnosis === 'string' ? dx.diagnosis : null)
    || fit(text(selected[0]))
  const diagnose = isCompleted ? named : null

  // -- DELIVER: the disposition, from durable care-plan data -----------------
  //   NOT from currentCase.deliverState alone: that is written only by
  //   DeliverScreen and is undefined until the patient opens Deliver, which is
  //   why the card reverted to "Locked / Treatment & Follow-up" on every cold
  //   launch even for a resolved case — TF b37 #10's screenshot exactly.
  const plan = carePlan || {}
  const disposition = plan.followUpPlan || currentCase.deliverState || null
  const DISPOSITIONS = {
    observation: t('caseHome.blurbObserving', 'Monitoring your symptoms'),
    complete: t('caseHome.blurbCareComplete', 'Care complete'),
    completed: t('caseHome.blurbCareComplete', 'Care complete'),
    'all-done': t('caseHome.blurbCareComplete', 'Care complete'),
    pcp: t('caseHome.blurbBackToPcp', 'Care complete — back to your regular doctor'),
    referral: t('caseHome.blurbReferral', 'Referred to a specialist'),
    refer: t('caseHome.blurbReferral', 'Referred to a specialist'),
    scheduled: t('caseHome.blurbFollowUpScheduled', 'Follow-up scheduled'),
    'follow-up': t('caseHome.blurbFollowUpPlanned', 'Follow-up planned'),
    submitted: t('caseHome.blurbSubmitted', 'Sent to your doctor'),
  }
  const procedureCount = Array.isArray(plan.procedures) ? plan.procedures.length : 0
  const deliver = DISPOSITIONS[disposition]
    || (procedureCount > 0 ? t('caseHome.blurbProcedurePlanned', 'Procedure planned') : null)

  const resolved = ['complete', 'completed', 'pcp'].includes(plan.followUpPlan)
    || currentCase.deliverState === 'all-done' || currentCase.deliverState === 'complete'

  return { describe, detect, diagnose, deliver, resolved }
}
